import { Link } from "@remix-run/react";
import { SimpleTooltip as Tooltip } from "#app/components/ui/tooltip";
import { Button } from "#app/components/ui/button";
import { Modal } from "#app/components/new-ui/modal";
import { CircleHelp } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const IDLE_MS = 100_000;
const MAX_AUTO_SHOWS_PER_SESSION = 1;
const AUTO_SHOW_STORAGE_KEY = "workflow-chat-tips:auto-show-count";

function getAutoShowCount(): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.sessionStorage.getItem(AUTO_SHOW_STORAGE_KEY);
    if (!raw) return 0;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

function incrementAutoShowCount(): number {
  if (typeof window === "undefined") return 0;
  try {
    const next = getAutoShowCount() + 1;
    window.sessionStorage.setItem(AUTO_SHOW_STORAGE_KEY, String(next));
    return next;
  } catch {
    return MAX_AUTO_SHOWS_PER_SESSION;
  }
}

type PanelMode = "nudge" | "full";

function WorkflowTipsFullBody() {
  const { t } = useTranslation("chat");

  const bestPractices =
    (t("workflowTips.bestPractices", { returnObjects: true }) as
      | { title: string; body: string }[]
      | undefined) ?? [];
  const quickFixes =
    (t("workflowTips.quickFixes", { returnObjects: true }) as
      | { situation: string; action: string }[]
      | undefined) ?? [];

  return (
    <div className="space-y-5 px-4 py-3">
      <p className="text-xs text-muted-foreground">{t("workflowTips.intro")}</p>

      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t("workflowTips.bestPracticesTitle")}
        </h3>
        <ul className="list-disc space-y-2 pl-4 text-sm text-foreground">
          {bestPractices.map((item) => (
            <li key={item.title}>
              <span className="font-medium">{item.title}.</span> {item.body}
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t("workflowTips.quickFixesTitle")}
        </h3>
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full min-w-[280px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th
                  scope="col"
                  className="px-3 py-2 font-semibold text-foreground"
                >
                  {t("workflowTips.tableSituation")}
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 font-semibold text-foreground"
                >
                  {t("workflowTips.tableAction")}
                </th>
              </tr>
            </thead>
            <tbody>
              {quickFixes.map((row) => (
                <tr
                  key={row.situation}
                  className="border-b border-border last:border-b-0"
                >
                  <td className="align-top px-3 py-2 text-foreground">
                    {row.situation}
                  </td>
                  <td className="align-top px-3 py-2 text-muted-foreground">
                    {row.action}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="space-y-2 border-t border-border pt-3 text-sm">
        <p className="text-foreground">
          <span className="font-medium">{t("workflowTips.stillStuck")}</span>{" "}
          <Link
            to="/support"
            className="font-medium text-[#535EA4] underline-offset-4 hover:underline"
          >
            {t("workflowTips.support")}
          </Link>
          {" · "}
          <Link
            to="/docs"
            className="font-medium text-[#535EA4] underline-offset-4 hover:underline"
          >
            {t("workflowTips.documentation")}
          </Link>
          {" · "}
          <Link
            to="/u/alpha/tutorials"
            className="font-medium text-[#535EA4] underline-offset-4 hover:underline"
          >
            {t("workflowTips.tutorials")}
          </Link>
          {" · "}
          <Link
            to="/u/community/new"
            className="font-medium text-[#535EA4] underline-offset-4 hover:underline"
          >
            {t("workflowTips.askCommunity")}
          </Link>
        </p>
      </div>
    </div>
  );
}

export function WorkflowChatTips({
  pauseWhileStreaming,
}: {
  /** When true, idle timer does not run and the panel will not auto-open (assistant is streaming). */
  pauseWhileStreaming?: boolean;
}) {
  const { t } = useTranslation("chat");
  const [open, setOpen] = useState(false);
  const [panelMode, setPanelMode] = useState<PanelMode>("nudge");
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pauseWhileStreamingRef = useRef(pauseWhileStreaming);
  const panelModeRef = useRef(panelMode);
  const autoShowCapReachedRef = useRef(false);
  pauseWhileStreamingRef.current = pauseWhileStreaming;
  panelModeRef.current = panelMode;

  if (
    typeof window !== "undefined" &&
    !autoShowCapReachedRef.current &&
    getAutoShowCount() >= MAX_AUTO_SHOWS_PER_SESSION
  ) {
    autoShowCapReachedRef.current = true;
  }

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setPanelMode("nudge");
  }, []);

  const openFullTips = useCallback(() => {
    setPanelMode("full");
    setOpen(true);
  }, []);

  const scheduleIdleOpen = useCallback(() => {
    clearIdleTimer();
    if (open || pauseWhileStreaming) return;
    if (autoShowCapReachedRef.current) return;

    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      if (pauseWhileStreamingRef.current) return;
      if (autoShowCapReachedRef.current) return;

      const nextCount = incrementAutoShowCount();
      if (nextCount >= MAX_AUTO_SHOWS_PER_SESSION) {
        autoShowCapReachedRef.current = true;
      }
      setPanelMode("nudge");
      setOpen(true);
    }, IDLE_MS);
  }, [clearIdleTimer, open, pauseWhileStreaming]);

  useEffect(() => {
    if (autoShowCapReachedRef.current) return;

    const onActivity = () => {
      scheduleIdleOpen();
    };

    const opts: AddEventListenerOptions = { passive: true };
    window.addEventListener("pointerdown", onActivity, opts);
    window.addEventListener("keydown", onActivity);
    window.addEventListener("scroll", onActivity, opts);

    scheduleIdleOpen();

    return () => {
      window.removeEventListener("pointerdown", onActivity);
      window.removeEventListener("keydown", onActivity);
      window.removeEventListener("scroll", onActivity);
      clearIdleTimer();
    };
  }, [clearIdleTimer, scheduleIdleOpen]);

  useEffect(() => {
    if (autoShowCapReachedRef.current) {
      clearIdleTimer();
      return;
    }
    if (open || pauseWhileStreaming) {
      clearIdleTimer();
    } else {
      scheduleIdleOpen();
    }
  }, [open, pauseWhileStreaming, clearIdleTimer, scheduleIdleOpen]);

  useEffect(() => {
    if (!pauseWhileStreaming) return;
    if (open && panelModeRef.current === "nudge") {
      setOpen(false);
      setPanelMode("nudge");
    }
  }, [pauseWhileStreaming, open]);

  const isNudge = panelMode === "nudge";

  return (
    <>
      <Tooltip title={t("workflowTips.tooltip")} className="max-w-[280px]">
        <button
          type="button"
          aria-label={t("workflowTips.ariaLabel")}
          onClick={openFullTips}
          className={`relative rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            open ? "text-[#535EA4]" : ""
          }`}
        >
          <CircleHelp className="h-4 w-4" aria-hidden />
        </button>
      </Tooltip>

      <Modal
        open={open}
        onCancel={handleClose}
        centered
        width={isNudge ? 352 : 544}
        className="workflow-chat-tips-modal"
        title={
          isNudge ? t("workflowTips.nudgeTitle") : t("workflowTips.fullTitle")
        }
        footer={
          isNudge
            ? [
                <Button
                  key="view"
                  className="bg-[#535EA4]! hover:bg-[#434d8a]!"
                  onClick={() => setPanelMode("full")}
                >
                  {t("workflowTips.viewTips")}
                </Button>,
                <Button variant="ghost" key="dismiss" onClick={handleClose}>
                  {t("workflowTips.notNow")}
                </Button>,
              ]
            : null
        }
        styles={{
          body: {
            maxHeight: isNudge ? undefined : "min(78vh, 36rem)",
            overflowY: isNudge ? undefined : "auto",
            padding: isNudge ? undefined : 0,
          },
        }}
      >
        {isNudge ? (
          <div role="status">
            <p className="text-sm text-muted-foreground leading-snug">
              {t("workflowTips.nudgeBody")}
            </p>
          </div>
        ) : (
          <WorkflowTipsFullBody />
        )}
      </Modal>
    </>
  );
}
